import {
  addComponentDataFromTemplate,
  AirHandlingUnitData,
  changeNodes,
  ComponentData,
} from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import {
  Background,
  BackgroundVariant,
  Controls,
  NodeChange,
  NodeProps,
  Panel,
  ReactFlow,
  useReactFlow,
} from "@xyflow/react";
import { createContext, useContext, useMemo, useState } from "react";
import {
  AhuComponentCatalog,
  Item,
} from "@/services/backend/htz/ahu/types/ahu-component-catalog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuTrigger,
} from "@/shared/components/ui/context-menu";
import { ChevronDown, Trash2 } from "lucide-react";
import t from "@/lang/lang";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { useInspectionContext } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/inspection-context";
import { InspectionIndicator } from "@/services/backend/htz/ahu/components/indicators";
import { cn } from "@/shared/lib/utils";
import { useHtzAirHandlingUnitComponentPutMutation } from "@/services/backend/htz/ahu/api/ahu-component-api";
import { toast } from "sonner";
import { v4 } from "uuid";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";

export interface AhuComponentsSchemaProps {
  ahuData: AirHandlingUnitData;
  onAhuDataChange: (ahu: AirHandlingUnitData) => void;
  ahuComponentCatalog: AhuComponentCatalog;
  className?: string;
}

export function AhuComponentsSchema({
  ahuData,
  onAhuDataChange,
  ahuComponentCatalog,
  className,
}: AhuComponentsSchemaProps) {
  const value = useMemo(
    () => ({
      ahuData,
      onAhuDataChange,
      ahuComponentCatalog,
      immutable: false,
    }),
    [ahuData, onAhuDataChange, ahuComponentCatalog],
  );

  const { active, initialized } = useInspectionContext();
  const inspectionMode = initialized && active;

  // We use a context, since it is not possible to pass down
  // properties to nodes in the schema. Using a context allows
  // to define actions on the node or from a context menu (right click).
  return (
    <AhuSchemaContext.Provider value={value}>
      {inspectionMode ? (
        <InspectionModeSchema className={className} />
      ) : (
        <AhuSchema className={className} />
      )}
    </AhuSchemaContext.Provider>
  );
}

interface AhuSchemaContextInterface {
  ahuData: AirHandlingUnitData;
  onAhuDataChange: (ahu: AirHandlingUnitData) => void;
  ahuComponentCatalog: AhuComponentCatalog;
  immutable: boolean;
}

const AhuSchemaContext = createContext<AhuSchemaContextInterface>({
  ahuData: {} as AirHandlingUnitData,
  onAhuDataChange: () => null,
  ahuComponentCatalog: {} as AhuComponentCatalog,
  immutable: false,
});

function useAhuSchemaContext() {
  return useContext(AhuSchemaContext);
}

// 12 is a value working well together with tailwind sizes
const gridGap = 12;

function AhuSchema({ className }: { className?: string }) {
  const { ahuData, onAhuDataChange, ahuComponentCatalog } =
    useAhuSchemaContext();

  const { components: nodes } = ahuData;

  const onNodesChange = (changes: NodeChange<ComponentData>[]) =>
    onAhuDataChange(changeNodes(ahuData, changes));

  const nodeTypes = useMemo(
    () =>
      ahuComponentCatalog.items.reduce(
        (acc, template) => ({
          ...acc,
          // btw, TS is crazy...
          [template.type]: SchemaComponent,
        }),
        {},
      ),
    [ahuComponentCatalog],
  );

  return (
    <div className={cn("h-[600px] rounded-lg border", className)}>
      <ReactFlow
        nodeTypes={nodeTypes}
        nodes={nodes}
        onNodesChange={onNodesChange}
        snapToGrid
        snapGrid={[gridGap, gridGap]}
        fitView
        fitViewOptions={{
          padding: 1,
        }}
      >
        <AddComponentBar />
        <Controls />
        <Background variant={BackgroundVariant.Dots} gap={gridGap} size={1} />
      </ReactFlow>
    </div>
  );
}

function AddComponentBar() {
  const { ahuData, onAhuDataChange, ahuComponentCatalog } =
    useAhuSchemaContext();
  const [put] = useHtzAirHandlingUnitComponentPutMutation();

  const onNodeAdd = async (template: Item) => {
    const now = new Date().toISOString();
    put({
      id: v4(),
      ahuId: ahuData.airHandlingUnitId,
      companyId: ahuData.companyId,
      name: template.name,
      createdAt: now,
      updatedAt: now,
    })
      .unwrap()
      .then((component) => {
        // mutate the ahu data
        onAhuDataChange(
          addComponentDataFromTemplate(ahuData, component.id, template),
        );
      })
      .catch((error) => {
        toast(...parseRTKQueryErrorToSonner(error, false));
      });
  };

  const withoutGroup = ahuComponentCatalog.items.filter(
    (temp) => temp.group === "",
  );
  const groups = ahuComponentCatalog.items.reduce((prev, cur) => {
    // if without group, do nothing
    if (cur.group === "") {
      return prev;
    }

    // init group
    if (!prev.has(cur.group)) {
      prev.set(cur.group, []);
    }

    // add template
    prev.set(cur.group, [...prev.get(cur.group)!, cur]);

    return prev;
  }, new Map<string, Item[]>());

  // this roughly fits well, for 1-4 rows, with is the expected range
  const rows = Math.ceil(withoutGroup.length / 12);
  const separatorHeight = rows * 2 + rows * 8;

  return (
    <Panel
      position="top-center"
      className="bg-background flex min-w-max items-start gap-4 space-x-2 rounded-none border px-3 py-2 shadow-xs"
    >
      <div className="grid grid-cols-4 gap-2 md:grid-cols-6 lg:grid-cols-12">
        {withoutGroup.map((template) => (
          <AddComponentButton
            template={template}
            onAdd={onNodeAdd}
            key={template.type}
          />
        ))}
      </div>
      <Separator orientation="vertical" className={`h-${separatorHeight}`} />
      <div className="flex flex-col space-y-2">
        {Array.from(groups.keys()).map((group) => (
          <AddComponentGroupPopover
            templates={groups.get(group)!}
            onAdd={onNodeAdd}
            key={group}
          />
        ))}
      </div>
    </Panel>
  );
}

function AddComponentGroupPopover({
  templates,
  onAdd,
}: {
  templates: Item[];
  onAdd: (c: Item) => void;
}) {
  const [open, setOpen] = useState(false);

  if (templates.length === 0) {
    return null;
  }

  const groupName = templates[0].group;

  const doAdd = (template: Item) => {
    onAdd(template);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <span>{groupName}</span>
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-max" align="end">
        <div className="mb-4 text-sm font-bold">{groupName}</div>
        <div className="bg-background grid grid-cols-6 gap-2">
          {templates.map((template) => (
            <div key={template.id}>
              <AddComponentButton template={template} onAdd={doAdd} />
              <div className="text-sm tracking-tighter">{template.details}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AddComponentButton({
  template,
  onAdd,
}: {
  template: Item;
  onAdd: (c: Item) => void;
}) {
  const doAdd = () => onAdd(template);

  return (
    <button
      type="button"
      onClick={doAdd}
      className="bg-background hover:bg-background flex items-center justify-center p-0"
    >
      <img
        src={`data:image/svg+xml;base64,${template.img}`}
        alt={template.name}
        className="h-9 max-w-9"
      />
    </button>
  );
}

function SchemaComponent({ id, data }: NodeProps<ComponentData>) {
  const { deleteElements } = useReactFlow();

  // Components (the imgs) are expected to be defined in a
  // fixed aspect ratio and same size.
  // The sizeFactor is used to scale them as configured.
  const width = 12 * data.sizeFactor;

  const onDelete = async () => {
    await deleteElements({
      nodes: [{ id }],
    });
  };

  return (
    <div>
      <ContextMenu>
        <ContextMenuTrigger>
          <div className="relative">
            <img
              src={`data:image/svg+xml;base64,${data.img}`}
              alt={data.name}
              className={`w-${width} bg-white`}
            />
            <div className="absolute top-12 left-2 text-xs font-bold tracking-tight">
              {data.details}
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuLabel>{data.name}</ContextMenuLabel>
          <ContextMenuItem onClick={onDelete}>
            <Trash2 className="mr-2 h-4 w-4" />
            <span>{t("Löschen")}</span>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </div>
  );
}

function InspectionModeSchema({ className }: { className?: string }) {
  const { ahuData: ahu, ahuComponentCatalog } = useAhuSchemaContext();
  const { components: nodes } = ahu;

  const nodeTypes = useMemo(
    () =>
      ahuComponentCatalog.items.reduce(
        (acc, template) => ({
          ...acc,
          // btw, TS is crazy...
          [template.type]: InspectionModeSchemaComponent,
        }),
        {},
      ),
    [ahuComponentCatalog],
  );

  return (
    <div className={cn("h-[600px] rounded-lg border", className)}>
      <ReactFlow
        nodes={nodes}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        snapGrid={[gridGap, gridGap]}
        fitView
        fitViewOptions={{
          padding: 0.25,
        }}
      >
        <Controls />
        <Background variant={BackgroundVariant.Dots} gap={gridGap} size={1} />
      </ReactFlow>
    </div>
  );
}

function InspectionModeSchemaComponent({ id, data }: NodeProps<ComponentData>) {
  const { activeComponent, setActiveComponent } = useInspectionContext();
  const { ahuData } = useAhuSchemaContext();
  const component = ahuData.components.find((c) => c.id === id);

  // Components (the imgs) are expected to be defined in a
  // fixed aspect ratio and same size.
  // The sizeFactor is used to scale them as configured.
  const width = 12 * data.sizeFactor;

  const active = id === activeComponent?.id;

  const onClick = () => {
    if (active) {
      setActiveComponent(null);
    } else {
      setActiveComponent(component!);
    }
  };

  return (
    <div className="relative">
      <button onClick={onClick} type="button">
        <img
          src={`data:image/svg+xml;base64,${data.img}`}
          alt={data.name}
          className={cn(`w-${width} bg-white`, active && "bg-muted-foreground")}
        />
      </button>
      <div className="absolute top-12 left-2 text-xs font-bold tracking-tight">
        {data.details}
      </div>
      <div className="absolute top-1 left-1">
        <InspectionIndicator componentId={component!.componentId} />
      </div>
    </div>
  );
}
